import * as React from "react";
import {
  AlertTriangle,
  Banknote,
  Briefcase,
  CalendarArrowDown,
  CalendarArrowUp,
  CircleDollarSign,
  FileText,
  User,
  Wallet
} from "lucide-react";

import { cn } from "@/lib/utils";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ApprovalTimeline,
  TimelineNode
} from "@/components/requests/ApprovalTimeline";
import {
  DetailCard,
  DetailField,
  ReviewEditNote
} from "@/components/requests/DetailCard";
import { TripDetailFields } from "@/components/requests/TripRows";
import { fetchAdvance } from "../../api";
import { Advance, AdvanceStatus, CLAIM_METHOD_LABEL, OTHER_CLAIM_TYPE_LABEL } from "../../types";
import { formatDate, formatDateTime } from "@/lib/datetime";

// Employee-facing wording for an advance's status - "Resubmit" reads as an
// instruction, not a state. Colour comes from the shared StatusBadge.
const ADVANCE_STATUS_LABEL: Record<AdvanceStatus, string> = {
  Pending: "Pending",
  Approved: "Approved",
  Rejected: "Rejected",
  Paid: "Paid",
  Resubmit: "Needs changes"
};

// Build the Requestor → Approver → Finance approver timeline from the real
// advance state (see db/advances_store.py for the state machine). The detail
// API resolves only the requester's name, so the approver nodes are labelled
// by role. financeDecision is only ever a Finance REJECTION; a Rejected
// advance without one was rejected by the Manager instead, ending the journey
// at the Approver node (Finance "skipped").
function buildAdvanceTimeline(detail: Advance): TimelineNode[] {
  const requesterName =
    detail.requester?.displayName || detail.requester?.email || "Requestor";
  // Approver names snapshotted onto the records at creation (see
  // db/advances_store.py) - the Manager's on `approval`, the Finance
  // Approver's on `financeDecision` (only ever set on a Finance rejection).
  // Fall back to the generic role label where we have no stored name.
  const managerName = detail.approval?.approverName || "Reporting manager";
  // financeDecision is only set on a Finance rejection, so otherwise fall back
  // to the finance approver the requester picked at submission (snapshotted on
  // the advance) rather than the generic label.
  const financeName =
    detail.financeDecision?.approverName ||
    detail.financeApproverNameAtSubmission ||
    "Finance team";
  const financeRejected = !!detail.financeDecision;
  // A requester with no reporting manager has the Manager stage skipped - the
  // advance's single `approval` record is created against the FINANCE approver
  // they picked (see requests_api.py's create_advance no-manager branch), so
  // its approverId matches financeApproverIdAtSubmission. Detect that so the
  // Manager node reads as skipped and the Finance node reflects that record.
  const managerSkipped =
    !!detail.approval?.approverId &&
    detail.approval.approverId === detail.financeApproverIdAtSubmission;
  const managerRejected =
    detail.status === "Rejected" && !financeRejected && !managerSkipped;
  const managerApproved =
    detail.approval?.status === "Approved" ||
    detail.status === "Approved" ||
    detail.status === "Paid" ||
    financeRejected;

  const nodes: TimelineNode[] = [
    {
      role: "Requestor",
      name: requesterName,
      state: "done",
      statusLabel: "Submitted"
    }
  ];

  // Resubmit: the approval sub-document is deliberately left untouched
  // (still whatever it was) so the employee's resubmission routes back to
  // the same approver - see AdvanceApprovalStore.resubmit. Shown explicitly
  // rather than falling through to the normal pending/approved checks
  // below, same reasoning as RequestDetailDialog's own ResubmitRequested
  // branch.
  if (detail.status === "Resubmit") {
    const sentBackByFinance = detail.resubmitRequestedBy === "Finance";
    nodes.push({
      role: "Approver",
      name: managerName,
      state: sentBackByFinance ? "approved" : "rejected",
      statusLabel: sentBackByFinance ? "Approved" : "Sent back for changes"
    });
    nodes.push({
      role: "Finance approver",
      name: financeName,
      state: sentBackByFinance ? "rejected" : "skipped",
      statusLabel: sentBackByFinance
        ? "Sent back for changes"
        : "Not reached yet"
    });
    return nodes;
  }

  if (managerRejected) {
    nodes.push({
      role: "Approver",
      name: managerName,
      state: "rejected",
      statusLabel: "Rejected"
    });
    nodes.push({
      role: "Finance approver",
      name: financeName,
      state: "skipped",
      statusLabel: "Not reached"
    });
    return nodes;
  }

  if (managerSkipped) {
    // No reporting manager - the Manager stage is auto-approved (consistent
    // with the travel-request flow), the advance having gone straight to the
    // picked Finance Approver.
    nodes.push({
      role: "Approver",
      name: "Auto-approved",
      state: "approved",
      statusLabel: "Auto-approved"
    });
  } else {
    nodes.push({
      role: "Approver",
      name: managerName,
      state: managerApproved ? "approved" : "pending",
      statusLabel: managerApproved ? "Approved" : "Pending"
    });
  }

  // Finance node. When the Manager stage was skipped, the `approval` record IS
  // the Finance stage - a Rejected status there is a Finance rejection.
  const financeNameResolved = managerSkipped
    ? detail.approval?.approverName || financeName
    : financeName;
  if (financeRejected || (managerSkipped && detail.status === "Rejected")) {
    nodes.push({
      role: "Finance approver",
      name: financeNameResolved,
      state: "rejected",
      statusLabel: "Rejected"
    });
  } else if (detail.status === "Paid") {
    nodes.push({
      role: "Finance approver",
      name: financeNameResolved,
      state: "approved",
      statusLabel: "Paid"
    });
  } else {
    nodes.push({
      role: "Finance approver",
      name: financeNameResolved,
      state: "pending",
      statusLabel: "Pending"
    });
  }
  return nodes;
}

// Read-only detail card a Manager opens before deciding on an advance
// request - mirrors RequestDetailDialog's shape for travel requests, but
// for the standalone Advance entity (see db/advances_store.py), which has
// no bills to show.
export function AdvanceDetailDialog({
  advanceId,
  open,
  onOpenChange,
  highlight = false,
  footerActions
}: {
  advanceId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Same one-shot highlight-on-open as RequestDetailDialog's own prop -
   *  see its doc comment. */
  highlight?: boolean;
  /** Same footer-actions slot as RequestDetailDialog's own prop - see its
   *  doc comment for the full reasoning (this dialog stays action-agnostic,
   *  the caller builds whatever's appropriate for ITS context). Unlike the
   *  travel-request sheet, there's no Download report button here to
   *  balance against, so this just renders right-aligned - SheetFooter's
   *  own default. */
  footerActions?: React.ReactNode;
}) {
  const [detail, setDetail] = React.useState<Advance | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open || !advanceId) {
      setDetail(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    fetchAdvance(advanceId)
      .then(setDetail)
      .catch(() => setError("Could not load this advance."))
      .finally(() => setLoading(false));
  }, [open, advanceId]);

  const rejectionReason =
    detail && detail.status === "Rejected"
      ? detail.financeDecision?.comment || detail.approval?.comment
      : null;
  const approverComment =
    detail && detail.status !== "Rejected"
      ? detail.financeDecision?.comment || detail.approval?.comment
      : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        key={highlight ? advanceId : "no-highlight"}
        className={cn(
          "flex w-full flex-col gap-0 sm:max-w-xl",
          highlight && "animate-highlight-flash"
        )}
      >
        <SheetHeader>
          <SheetTitle>Advance details</SheetTitle>
          <SheetDescription>
            Everything submitted with this advance request.
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="-mx-6 mt-4 flex-1">
          <div className="px-6">
            {loading && (
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
              </div>
            )}

            {error && <p className="text-sm text-destructive">{error}</p>}

            {!loading && detail && (
              <div className="space-y-4 text-sm">
                <ApprovalTimeline nodes={buildAdvanceTimeline(detail)} />

                <DetailCard icon={FileText} title="Request details">
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
                    {detail.requester &&
                      (detail.requester.displayName ||
                        detail.requester.email) && (
                        <DetailField
                          icon={User}
                          label="Requested by"
                          className="col-span-1 sm:col-span-2"
                          value={
                            <span>
                              {detail.requester.displayName || "-"}
                              {detail.requester.email && (
                                <span className="ml-1.5 font-normal text-muted-foreground">
                                  {detail.requester.email}
                                </span>
                              )}
                            </span>
                          }
                        />
                      )}
                    <DetailField
                      icon={Briefcase}
                      label="Designation"
                      value={detail.requester?.designation || "-"}
                    />
                    {detail.requestKind === "other" ? (
                      // An Other reimbursement advance has no trip.
                      <>
                        <DetailField
                          icon={Wallet}
                          label="Claim type"
                          value={detail.claimType ? OTHER_CLAIM_TYPE_LABEL[detail.claimType] : "-"}
                        />
                        <DetailField
                          icon={CalendarArrowUp}
                          label="Planned date"
                          value={
                            detail.advanceDate
                              ? formatDate(detail.advanceDate)
                              : "-"
                          }
                        />
                      </>
                    ) : (
                      <>
                        <DetailField
                          icon={Wallet}
                          label="Claim method"
                          value={
                            detail.claimMethod
                              ? CLAIM_METHOD_LABEL[detail.claimMethod]
                              : "-"
                          }
                        />
                        <TripDetailFields trip={detail} />
                        <DetailField
                          icon={CalendarArrowUp}
                          label="Departure"
                          value={formatDateTime(detail.departureAt)}
                        />
                        <DetailField
                          icon={CalendarArrowDown}
                          label="Arrival"
                          value={formatDateTime(detail.arrivalAt)}
                        />
                      </>
                    )}
                    <DetailField
                      icon={CircleDollarSign}
                      label={detail.amounts.length > 1 ? "Amounts" : "Amount"}
                      value={
                        // One line per currency requested.
                        <span className="block space-y-0.5">
                          {detail.amounts.map((a) => (
                            <span key={a.currency} className="block tabular-nums">
                              {a.currency} {a.amount.toLocaleString()}
                            </span>
                          ))}
                        </span>
                      }
                    />
                    <DetailField
                      icon={Banknote}
                      label="Status"
                      value={
                        <StatusBadge
                          status={detail.status}
                          label={ADVANCE_STATUS_LABEL[detail.status]}
                        />
                      }
                    />
                  </dl>
                </DetailCard>

                {/* Optional on either stage's decision - financeDecision only
                ever exists for a Finance rejection (see types.ts), so a
                Rejected advance with no financeDecision was rejected by the
                Manager instead and approval.comment is the reason. */}
                {rejectionReason && (
                  <DetailCard
                    icon={AlertTriangle}
                    title="Rejection details"
                    tone="destructive"
                  >
                    <DetailField
                      icon={FileText}
                      label="Rejection reason"
                      value={
                        <span className="whitespace-pre-wrap">
                          {rejectionReason}
                        </span>
                      }
                    />
                  </DetailCard>
                )}

                {approverComment && (
                  <DetailCard icon={FileText} title="Approver comment">
                    <p className="whitespace-pre-wrap">{approverComment}</p>
                  </DetailCard>
                )}

                {detail.status === "Resubmit" && detail.resubmitComment && (
                  <DetailCard
                    icon={AlertTriangle}
                    title={`Changes requested by ${detail.resubmitRequestedBy === "Finance" ? "Finance" : "the reporting manager"}`}
                    tone="destructive"
                  >
                    <p className="whitespace-pre-wrap">
                      {detail.resubmitComment}
                    </p>
                  </DetailCard>
                )}

                {detail.reviewEdit && (
                  <ReviewEditNote reviewEdit={detail.reviewEdit} />
                )}

                {detail.flightDetails && (
                  <DetailCard
                    icon={FileText}
                    title={detail.requestKind === "other" ? "Purpose" : "Purpose of travel"}
                  >
                    <p className="whitespace-pre-wrap">
                      {detail.flightDetails}
                    </p>
                  </DetailCard>
                )}

                {detail.reason && (
                  <DetailCard icon={FileText} title="Any Other Request">
                    <p className="whitespace-pre-wrap">{detail.reason}</p>
                  </DetailCard>
                )}
              </div>
            )}
          </div>
        </ScrollArea>

        {!loading && detail && footerActions && (
          <SheetFooter className="mt-4 gap-2 sm:gap-2">
            {footerActions}
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}
